import React, { useState, useRef, useEffect, useCallback } from 'react';
import ClientDashboard from './ClientDashboard';
import Profile from '../profile';
import Charges from './Charges';
import Support from '../support';
import { HelpCircle, LogOut, ChevronDown, Settings, Receipt, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../common/Avatar';
import VersionPill from '../common/VersionPill';
import { ClientPortalProps, MenuItem } from '../../types';
import { clearTOTPSession } from '../../utils/totpSession';
import { refreshToken } from '../../api/petService';
import PmcCoBrand from '../common/PmcCoBrand';
import { PMC_EXPERIENCE_EVENT } from '../../api/pmcExperienceService';


const ClientPortal: React.FC<ClientPortalProps> = ({ theme, toggleTheme, appData, user }) => {
  const navigate = useNavigate();
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<string>('dashboard');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const [, setBrandingTick] = useState(0);

  useEffect(() => {
    const onBranding = () => setBrandingTick((t) => t + 1);
    window.addEventListener(PMC_EXPERIENCE_EVENT, onBranding);
    onBranding();
    return () => window.removeEventListener(PMC_EXPERIENCE_EVENT, onBranding);
  }, []);

  // --- Idle timeout: logout after 1 hour of no activity, refresh token while active ---
  const IDLE_TIMEOUT_MS = 60 * 60 * 1000;  // 1 hour
  const CHECK_INTERVAL_MS = 60 * 1000;      // check every 60s
  const THROTTLE_MS = 30 * 1000;            // update lastActive at most every 30s

  const lastActiveRef = useRef<number>(Date.now());
  const throttleRef = useRef<number>(0);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    if (now - throttleRef.current > THROTTLE_MS) {
      lastActiveRef.current = now;
      throttleRef.current = now;
    }
  }, []);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleUserActivity, { passive: true }));

    const enforceIdleOrRefresh = async () => {
      const idle = Date.now() - lastActiveRef.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        localStorage.removeItem('authToken');
        clearTOTPSession();
        sessionStorage.removeItem('sessionActive');
        window.location.href = '/login';
        return;
      }
      // User is still within the active window — refresh to keep the token alive.
      try { await refreshToken(); } catch (_) {}
    };

    const interval = setInterval(async () => {
      const idle = Date.now() - lastActiveRef.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        localStorage.removeItem('authToken');
        clearTOTPSession();
        sessionStorage.removeItem('sessionActive');
        window.location.href = '/login';
      } else if (idle < IDLE_TIMEOUT_MS / 2) {
        // User is active — silently refresh token to prevent server-side expiry
        try { await refreshToken(); } catch (_) {}
      }
    }, CHECK_INTERVAL_MS);

    // iOS Safari (and other mobile browsers) suspend setInterval while the tab
    // is backgrounded or the screen is locked, so the timer above stops firing
    // and the access token can silently expire mid-session. Re-checking the
    // moment the tab returns to the foreground refreshes the token *before* the
    // resident's next tap fires a request that would otherwise 401 and bounce
    // them to login. pageshow also covers bfcache restores on iOS.
    const handleForeground = () => {
      if (document.visibilityState === 'visible') {
        void enforceIdleOrRefresh();
      }
    };
    const handlePageShow = () => { void enforceIdleOrRefresh(); };

    document.addEventListener('visibilitychange', handleForeground);
    window.addEventListener('focus', handleForeground);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      events.forEach(e => window.removeEventListener(e, handleUserActivity));
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleForeground);
      window.removeEventListener('focus', handleForeground);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [handleUserActivity]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          userButtonRef.current && !userButtonRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      setLoading(true);
      localStorage.removeItem('authToken');
      clearTOTPSession();
      sessionStorage.removeItem('sessionActive');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNavClick = (viewId: string): void => {
    setCurrentView(viewId);
    setShowUserDropdown(false);
    setShowMobileNav(false);
  };

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Overview',
      icon: (
        <div className="w-6 h-6 relative">
          <svg viewBox="0 0 24 24" className="w-full h-full fill-current">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
        </div>
      ),
      active: currentView === 'dashboard'
    }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:flex w-64 bg-white border-r border-gray-200 shadow-lg flex-col">
        {/* Header/Branding */}
        <div className="h-[73px] border-b border-gray-200 flex items-center px-4">
          <div className="flex items-center cursor-pointer" onClick={() => { setCurrentView('dashboard'); navigate('/dashboard'); }}>
            <PmcCoBrand />
          </div>
        </div>

        {/* Menu Items */}
        <div className="flex-1 px-4 py-4">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  item.active
                    ? 'pmc-nav-active shadow-md'
                    : 'pmc-nav-idle hover:bg-gray-50'
                }`}
              >
                <div className={item.active ? 'text-white' : 'text-current'}>
                  {item.icon}
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right side: top bar + content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 z-40 flex-shrink-0">
          <div className="flex items-center justify-between px-4 sm:px-6 h-[73px]">
            {/* Mobile: Logo + Hamburger */}
            <div className="flex items-center gap-3 lg:hidden">
              <PmcCoBrand imgClassName="h-6 w-auto" pmcMaxWidth="max-w-[80px]" />
            </div>

            {/* Desktop: page title or spacer */}
            <div className="hidden lg:block" />

            {/* Right: Support + User */}
            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              {/* Version pill — fetches GET /api/version. Hidden on mobile. */}
              <VersionPill />

              {/* Support Button */}
              <button
                onClick={() => handleNavClick('support')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  currentView === 'support'
                    ? 'pmc-nav-active shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Support"
              >
                <HelpCircle className="w-5 h-5" />
                <span className="hidden sm:inline">Support</span>
              </button>

              {/* User Dropdown */}
              <div className="relative">
                <button
                  ref={userButtonRef}
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Avatar user={user} size="w-8 h-8" />
                  <span className="hidden sm:inline text-sm font-medium text-gray-900 max-w-[150px] truncate">
                    {user?.displayName || user?.email || 'User'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <Avatar 
                          user={user}
                          size="w-10 h-10" 
                          className="rounded-lg flex-shrink-0"
                          textSize="text-lg"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {user?.displayName || 'User'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => handleNavClick('profile')}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-400" />
                        <span>Profile Settings</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('charges')}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Receipt className="w-4 h-4 text-gray-400" />
                        <span>Charges</span>
                      </button>
                    </div>

                    {/* Divider + Logout */}
                    <div className="border-t border-gray-100 py-1">
                      <button
                        onClick={handleLogout}
                        disabled={loading}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{loading ? 'Logging out...' : 'Logout'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                onClick={() => setShowMobileNav(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Modal */}
        {showMobileNav && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div 
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setShowMobileNav(false)}
            />
            <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl">
              <div className="border-b border-gray-200 p-4 flex items-center justify-between">
                <PmcCoBrand imgClassName="h-6 w-auto" pmcMaxWidth="max-w-[80px]" />
                <button onClick={() => setShowMobileNav(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="px-4 py-6 space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      item.active ? 'pmc-nav-active shadow-md' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className={item.active ? 'text-white' : 'text-current'}>{item.icon}</div>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="border-t border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Avatar user={user} size="w-10 h-10" className="rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{user?.displayName || 'User'}</div>
                      <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowMobileNav(false); handleLogout(); }}
                    disabled={loading}
                    className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 overflow-auto">
          {currentView === 'dashboard' && <ClientDashboard appData={appData} user={user} />}
          {currentView === 'profile' && <Profile onBack={() => setCurrentView('dashboard')} user={user} />}
          {currentView === 'charges' && <Charges />}
          {currentView === 'support' && <Support onBack={() => setCurrentView('dashboard')} />}
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;